/* @ts-self-types="./libparsec_bindings_web.d.ts" */

/**
 * Email address struct.
 *
 * # Examples
 * ```
 * use email_address_parser::EmailAddress;
 *
 * assert!(EmailAddress::parse("foo@-bar.com", None).is_none());
 * let email = EmailAddress::parse("foo@bar.com", None);
 * assert!(email.is_some());
 * let email = email.unwrap();
 * assert_eq!(email.get_local_part(), "foo");
 * assert_eq!(email.get_domain(), "bar.com");
 * assert_eq!(format!("{}", email), "foo@bar.com");
 * ```
 */
export class EmailAddress {
    static __wrap(ptr) {
        ptr = ptr >>> 0;
        const obj = Object.create(EmailAddress.prototype);
        obj.__wbg_ptr = ptr;
        EmailAddressFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EmailAddressFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_emailaddress_free(ptr, 0);
    }
    /**
     * This is a WASM wrapper over EmailAddress::new that panics.
     * If you are using this lib from Rust then consider using EmailAddress::new.
     *
     * # Examples
     * ```
     * use email_address_parser::EmailAddress;
     *
     * let email = EmailAddress::_new("foo", "bar.com", None);
     * ```
     *
     * # Panics
     *
     * This method panics if the local part or domain is invalid.
     *
     * ```rust,should_panic
     * use email_address_parser::EmailAddress;
     *
     * EmailAddress::_new("foo", "-bar.com", None);
     * ```
     * @param {string} local_part
     * @param {string} domain
     * @param {ParsingOptions | null} [options]
     */
    constructor(local_part, domain, options) {
        const ptr0 = passStringToWasm0(local_part, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(domain, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        let ptr2 = 0;
        if (!isLikeNone(options)) {
            _assertClass(options, ParsingOptions);
            ptr2 = options.__destroy_into_raw();
        }
        const ret = wasm.emailaddress__new(ptr0, len0, ptr1, len1, ptr2);
        this.__wbg_ptr = ret >>> 0;
        EmailAddressFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Returns the domain of the email address.
     *
     * Note that if you are using this library from rust, then consider using the `get_domain` method instead.
     * This returns a cloned copy of the domain string, instead of a borrowed `&str`, and exists purely for WASM interoperability.
     *
     * # Examples
     * ```
     * use email_address_parser::EmailAddress;
     *
     * let email = EmailAddress::new("foo", "bar.com", None).unwrap();
     * assert_eq!(email.domain(), "bar.com");
     *
     * let email = EmailAddress::parse("foo@bar.com", None).unwrap();
     * assert_eq!(email.domain(), "bar.com");
     * ```
     * @returns {string}
     */
    get domain() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.emailaddress_domain(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Validates if the given `input` string is an email address or not.
     *
     * Returns `true` if the `input` is valid, `false` otherwise.
     * Unlike the `parse` method, it does not instantiate an `EmailAddress`.
     * # Examples
     * ```
     * use email_address_parser::*;
     *
     * // strict validation
     * assert!(EmailAddress::is_valid("foo@bar.com", None));
     *
     * // non-strict validation
     * assert!(EmailAddress::is_valid("\u{0d}\u{0a} \u{0d}\u{0a} test@iana.org", Some(ParsingOptions::new(true))));
     *
     * // invalid address
     * assert!(!EmailAddress::is_valid("test@-iana.org", Some(ParsingOptions::new(true))));
     * assert!(!EmailAddress::is_valid("test@-iana.org", Some(ParsingOptions::new(true))));
     * assert!(!EmailAddress::is_valid("test", Some(ParsingOptions::new(true))));
     * assert!(!EmailAddress::is_valid("test", Some(ParsingOptions::new(true))));
     * ```
     * @param {string} input
     * @param {ParsingOptions | null} [options]
     * @returns {boolean}
     */
    static isValid(input, options) {
        const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        let ptr1 = 0;
        if (!isLikeNone(options)) {
            _assertClass(options, ParsingOptions);
            ptr1 = options.__destroy_into_raw();
        }
        const ret = wasm.emailaddress_isValid(ptr0, len0, ptr1);
        return ret !== 0;
    }
    /**
     * Returns the local part of the email address.
     *
     * Note that if you are using this library from rust, then consider using the `get_local_part` method instead.
     * This returns a cloned copy of the local part string, instead of a borrowed `&str`, and exists purely for WASM interoperability.
     *
     * # Examples
     * ```
     * use email_address_parser::EmailAddress;
     *
     * let email = EmailAddress::new("foo", "bar.com", None).unwrap();
     * assert_eq!(email.localPart(), "foo");
     *
     * let email = EmailAddress::parse("foo@bar.com", None).unwrap();
     * assert_eq!(email.localPart(), "foo");
     * ```
     * @returns {string}
     */
    get localPart() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.emailaddress_localPart(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Parses a given string as an email address.
     *
     * Accessible from WASM.
     *
     * Returns `Some(EmailAddress)` if the parsing is successful, else `None`.
     * # Examples
     * ```
     * use email_address_parser::*;
     *
     * // strict parsing
     * let email = EmailAddress::parse("foo@bar.com", None);
     * assert!(email.is_some());
     * let email = email.unwrap();
     * assert_eq!(email.get_local_part(), "foo");
     * assert_eq!(email.get_domain(), "bar.com");
     *
     * // non-strict parsing
     * let email = EmailAddress::parse("\u{0d}\u{0a} \u{0d}\u{0a} test@iana.org", Some(ParsingOptions::new(true)));
     * assert!(email.is_some());
     *
     * // parsing invalid address
     * let email = EmailAddress::parse("test@-iana.org", Some(ParsingOptions::new(true)));
     * assert!(email.is_none());
     * let email = EmailAddress::parse("test@-iana.org", Some(ParsingOptions::new(true)));
     * assert!(email.is_none());
     * let email = EmailAddress::parse("test", Some(ParsingOptions::new(true)));
     * assert!(email.is_none());
     * let email = EmailAddress::parse("test", Some(ParsingOptions::new(true)));
     * assert!(email.is_none());
     * ```
     * @param {string} input
     * @param {ParsingOptions | null} [options]
     * @returns {EmailAddress | undefined}
     */
    static parse(input, options) {
        const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        let ptr1 = 0;
        if (!isLikeNone(options)) {
            _assertClass(options, ParsingOptions);
            ptr1 = options.__destroy_into_raw();
        }
        const ret = wasm.emailaddress_parse(ptr0, len0, ptr1);
        return ret === 0 ? undefined : EmailAddress.__wrap(ret);
    }
    /**
     * Returns the formatted EmailAddress.
     * This exists purely for WASM interoperability.
     * @returns {string}
     */
    toString() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.emailaddress_toString(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) EmailAddress.prototype[Symbol.dispose] = EmailAddress.prototype.free;

export class IntoUnderlyingByteSource {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntoUnderlyingByteSourceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_intounderlyingbytesource_free(ptr, 0);
    }
    /**
     * @returns {number}
     */
    get autoAllocateChunkSize() {
        const ret = wasm.intounderlyingbytesource_autoAllocateChunkSize(this.__wbg_ptr);
        return ret >>> 0;
    }
    cancel() {
        const ptr = this.__destroy_into_raw();
        wasm.intounderlyingbytesource_cancel(ptr);
    }
    /**
     * @param {ReadableByteStreamController} controller
     * @returns {Promise<any>}
     */
    pull(controller) {
        const ret = wasm.intounderlyingbytesource_pull(this.__wbg_ptr, controller);
        return ret;
    }
    /**
     * @param {ReadableByteStreamController} controller
     */
    start(controller) {
        wasm.intounderlyingbytesource_start(this.__wbg_ptr, controller);
    }
    /**
     * @returns {ReadableStreamType}
     */
    get type() {
        const ret = wasm.intounderlyingbytesource_type(this.__wbg_ptr);
        return __wbindgen_enum_ReadableStreamType[ret];
    }
}
if (Symbol.dispose) IntoUnderlyingByteSource.prototype[Symbol.dispose] = IntoUnderlyingByteSource.prototype.free;

export class IntoUnderlyingSink {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntoUnderlyingSinkFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_intounderlyingsink_free(ptr, 0);
    }
    /**
     * @param {any} reason
     * @returns {Promise<any>}
     */
    abort(reason) {
        const ptr = this.__destroy_into_raw();
        const ret = wasm.intounderlyingsink_abort(ptr, reason);
        return ret;
    }
    /**
     * @returns {Promise<any>}
     */
    close() {
        const ptr = this.__destroy_into_raw();
        const ret = wasm.intounderlyingsink_close(ptr);
        return ret;
    }
    /**
     * @param {any} chunk
     * @returns {Promise<any>}
     */
    write(chunk) {
        const ret = wasm.intounderlyingsink_write(this.__wbg_ptr, chunk);
        return ret;
    }
}
if (Symbol.dispose) IntoUnderlyingSink.prototype[Symbol.dispose] = IntoUnderlyingSink.prototype.free;

export class IntoUnderlyingSource {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntoUnderlyingSourceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_intounderlyingsource_free(ptr, 0);
    }
    cancel() {
        const ptr = this.__destroy_into_raw();
        wasm.intounderlyingsource_cancel(ptr);
    }
    /**
     * @param {ReadableStreamDefaultController} controller
     * @returns {Promise<any>}
     */
    pull(controller) {
        const ret = wasm.intounderlyingsource_pull(this.__wbg_ptr, controller);
        return ret;
    }
}
if (Symbol.dispose) IntoUnderlyingSource.prototype[Symbol.dispose] = IntoUnderlyingSource.prototype.free;

/**
 * Options for parsing.
 *
 * The is only one available option so far `is_lax` which can be set to
 * `true` or `false` to  enable/disable obsolete parts parsing.
 * The default is `false`.
 */
export class ParsingOptions {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsingOptionsFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsingoptions_free(ptr, 0);
    }
    /**
     * @returns {boolean}
     */
    get is_lax() {
        const ret = wasm.__wbg_get_parsingoptions_is_lax(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @param {boolean} is_lax
     */
    constructor(is_lax) {
        const ret = wasm.parsingoptions_new(is_lax);
        this.__wbg_ptr = ret >>> 0;
        ParsingOptionsFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * @param {boolean} arg0
     */
    set is_lax(arg0) {
        wasm.__wbg_set_parsingoptions_is_lax(this.__wbg_ptr, arg0);
    }
}
if (Symbol.dispose) ParsingOptions.prototype[Symbol.dispose] = ParsingOptions.prototype.free;

/**
 * @param {string} config_dir
 * @param {string} addr
 * @param {string} email
 * @returns {Promise<any>}
 */
export function accountCreate1SendValidationEmail(config_dir, addr, email) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.accountCreate1SendValidationEmail(ptr0, len0, ptr1, len1, ptr2, len2);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} addr
 * @param {string} validation_code
 * @param {string} email
 * @returns {Promise<any>}
 */
export function accountCreate2CheckValidationCode(config_dir, addr, validation_code, email) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(validation_code, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.accountCreate2CheckValidationCode(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} addr
 * @param {string} validation_code
 * @param {object} human_handle
 * @param {object} auth_method_strategy
 * @returns {Promise<any>}
 */
export function accountCreate3Proceed(config_dir, addr, validation_code, human_handle, auth_method_strategy) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(validation_code, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.accountCreate3Proceed(ptr0, len0, ptr1, len1, ptr2, len2, human_handle, auth_method_strategy);
    return ret;
}

/**
 * @param {number} account
 * @param {object} auth_method_strategy
 * @returns {Promise<any>}
 */
export function accountCreateAuthMethod(account, auth_method_strategy) {
    const ret = wasm.accountCreateAuthMethod(account, auth_method_strategy);
    return ret;
}

/**
 * @param {number} account
 * @param {object} existing_local_device_access
 * @returns {Promise<any>}
 */
export function accountCreateRegistrationDevice(account, existing_local_device_access) {
    const ret = wasm.accountCreateRegistrationDevice(account, existing_local_device_access);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountDelete1SendValidationEmail(account) {
    const ret = wasm.accountDelete1SendValidationEmail(account);
    return ret;
}

/**
 * @param {number} account
 * @param {string} validation_code
 * @returns {Promise<any>}
 */
export function accountDelete2Proceed(account, validation_code) {
    const ptr0 = passStringToWasm0(validation_code, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.accountDelete2Proceed(account, ptr0, len0);
    return ret;
}

/**
 * @param {number} account
 * @param {string} auth_method_id
 * @returns {Promise<any>}
 */
export function accountDisableAuthMethod(account, auth_method_id) {
    const ptr0 = passStringToWasm0(auth_method_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.accountDisableAuthMethod(account, ptr0, len0);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountInfo(account) {
    const ret = wasm.accountInfo(account);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountListAuthMethods(account) {
    const ret = wasm.accountListAuthMethods(account);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountListInvitations(account) {
    const ret = wasm.accountListInvitations(account);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountListOrganizations(account) {
    const ret = wasm.accountListOrganizations(account);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountListRegistrationDevices(account) {
    const ret = wasm.accountListRegistrationDevices(account);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} addr
 * @param {object} login_strategy
 * @returns {Promise<any>}
 */
export function accountLogin(config_dir, addr, login_strategy) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.accountLogin(ptr0, len0, ptr1, len1, login_strategy);
    return ret;
}

/**
 * @param {number} account
 * @returns {Promise<any>}
 */
export function accountLogout(account) {
    const ret = wasm.accountLogout(account);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} addr
 * @param {string} email
 * @returns {Promise<any>}
 */
export function accountRecover1SendValidationEmail(config_dir, addr, email) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.accountRecover1SendValidationEmail(ptr0, len0, ptr1, len1, ptr2, len2);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} addr
 * @param {string} validation_code
 * @param {string} email
 * @param {object} auth_method_strategy
 * @returns {Promise<any>}
 */
export function accountRecover2Proceed(config_dir, addr, validation_code, email, auth_method_strategy) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(validation_code, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.accountRecover2Proceed(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, auth_method_strategy);
    return ret;
}

/**
 * @param {number} account
 * @param {string} organization_id
 * @param {string} user_id
 * @param {string} new_device_label
 * @param {object} save_strategy
 * @returns {Promise<any>}
 */
export function accountRegisterNewDevice(account, organization_id, user_id, new_device_label, save_strategy) {
    const ptr0 = passStringToWasm0(organization_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(new_device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.accountRegisterNewDevice(account, ptr0, len0, ptr1, len1, ptr2, len2, save_strategy);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} device_path
 * @returns {Promise<any>}
 */
export function archiveDevice(config_dir, device_path) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(device_path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.archiveDevice(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {object} config
 * @param {string} bootstrap_organization_addr
 * @param {object} save_strategy
 * @param {object} human_handle
 * @param {string} device_label
 * @param {string | null} [sequester_authority_verify_key_pem]
 * @returns {Promise<any>}
 */
export function bootstrapOrganization(config, bootstrap_organization_addr, save_strategy, human_handle, device_label, sequester_authority_verify_key_pem) {
    const ptr0 = passStringToWasm0(bootstrap_organization_addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(sequester_authority_verify_key_pem) ? 0 : passStringToWasm0(sequester_authority_verify_key_pem, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len2 = WASM_VECTOR_LEN;
    const ret = wasm.bootstrapOrganization(config, ptr0, len0, save_strategy, human_handle, ptr1, len1, ptr2, len2);
    return ret;
}

/**
 * @param {string} hostname
 * @param {number | null | undefined} port
 * @param {boolean} use_ssl
 * @returns {Promise<any>}
 */
export function buildParsecAddr(hostname, port, use_ssl) {
    const ptr0 = passStringToWasm0(hostname, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.buildParsecAddr(ptr0, len0, isLikeNone(port) ? 0xFFFFFF : port, use_ssl);
    return ret;
}

/**
 * @param {string} addr
 * @param {string} organization_id
 * @returns {Promise<any>}
 */
export function buildParsecOrganizationBootstrapAddr(addr, organization_id) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(organization_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.buildParsecOrganizationBootstrapAddr(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {number} canceller
 * @returns {Promise<any>}
 */
export function cancel(canceller) {
    const ret = wasm.cancel(canceller);
    return ret;
}

/**
 * @param {number} handle
 * @param {object} save_strategy
 * @returns {Promise<any>}
 */
export function claimerDeviceFinalizeSaveLocalDevice(handle, save_strategy) {
    const ret = wasm.claimerDeviceFinalizeSaveLocalDevice(handle, save_strategy);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerDeviceInProgress1DoDenyTrust(canceller, handle) {
    const ret = wasm.claimerDeviceInProgress1DoDenyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerDeviceInProgress1DoSignifyTrust(canceller, handle) {
    const ret = wasm.claimerDeviceInProgress1DoSignifyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerDeviceInProgress2DoWaitPeerTrust(canceller, handle) {
    const ret = wasm.claimerDeviceInProgress2DoWaitPeerTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @param {string} requested_device_label
 * @returns {Promise<any>}
 */
export function claimerDeviceInProgress3DoClaim(canceller, handle, requested_device_label) {
    const ptr0 = passStringToWasm0(requested_device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.claimerDeviceInProgress3DoClaim(canceller, handle, ptr0, len0);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerDeviceInitialDoWaitPeer(canceller, handle) {
    const ret = wasm.claimerDeviceInitialDoWaitPeer(canceller, handle);
    return ret;
}

/**
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerGreeterAbortOperation(handle) {
    const ret = wasm.claimerGreeterAbortOperation(handle);
    return ret;
}

/**
 * @param {object} config
 * @param {string} addr
 * @returns {Promise<any>}
 */
export function claimerRetrieveInfo(config, addr) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.claimerRetrieveInfo(config, ptr0, len0);
    return ret;
}

/**
 * @param {number} recipient_pick_handle
 * @param {number} share_handle
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryAddShare(recipient_pick_handle, share_handle) {
    const ret = wasm.claimerShamirRecoveryAddShare(recipient_pick_handle, share_handle);
    return ret;
}

/**
 * @param {number} handle
 * @param {object} save_strategy
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryFinalizeSaveLocalDevice(handle, save_strategy) {
    const ret = wasm.claimerShamirRecoveryFinalizeSaveLocalDevice(handle, save_strategy);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryInProgress1DoDenyTrust(canceller, handle) {
    const ret = wasm.claimerShamirRecoveryInProgress1DoDenyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryInProgress1DoSignifyTrust(canceller, handle) {
    const ret = wasm.claimerShamirRecoveryInProgress1DoSignifyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryInProgress2DoWaitPeerTrust(canceller, handle) {
    const ret = wasm.claimerShamirRecoveryInProgress2DoWaitPeerTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryInProgress3DoClaim(canceller, handle) {
    const ret = wasm.claimerShamirRecoveryInProgress3DoClaim(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryInitialDoWaitPeer(canceller, handle) {
    const ret = wasm.claimerShamirRecoveryInitialDoWaitPeer(canceller, handle);
    return ret;
}

/**
 * @param {number} handle
 * @param {string} recipient_user_id
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryPickRecipient(handle, recipient_user_id) {
    const ptr0 = passStringToWasm0(recipient_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.claimerShamirRecoveryPickRecipient(handle, ptr0, len0);
    return ret;
}

/**
 * @param {number} handle
 * @param {string} requested_device_label
 * @returns {Promise<any>}
 */
export function claimerShamirRecoveryRecoverDevice(handle, requested_device_label) {
    const ptr0 = passStringToWasm0(requested_device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.claimerShamirRecoveryRecoverDevice(handle, ptr0, len0);
    return ret;
}

/**
 * @param {number} handle
 * @param {object} save_strategy
 * @returns {Promise<any>}
 */
export function claimerUserFinalizeSaveLocalDevice(handle, save_strategy) {
    const ret = wasm.claimerUserFinalizeSaveLocalDevice(handle, save_strategy);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerUserInProgress1DoDenyTrust(canceller, handle) {
    const ret = wasm.claimerUserInProgress1DoDenyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerUserInProgress1DoSignifyTrust(canceller, handle) {
    const ret = wasm.claimerUserInProgress1DoSignifyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerUserInProgress2DoWaitPeerTrust(canceller, handle) {
    const ret = wasm.claimerUserInProgress2DoWaitPeerTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @param {string} requested_device_label
 * @param {object} requested_human_handle
 * @returns {Promise<any>}
 */
export function claimerUserInProgress3DoClaim(canceller, handle, requested_device_label, requested_human_handle) {
    const ptr0 = passStringToWasm0(requested_device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.claimerUserInProgress3DoClaim(canceller, handle, ptr0, len0, requested_human_handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerUserInitialDoWaitPeer(canceller, handle) {
    const ret = wasm.claimerUserInitialDoWaitPeer(canceller, handle);
    return ret;
}

/**
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerUserListInitialInfo(handle) {
    const ret = wasm.claimerUserListInitialInfo(handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function claimerUserWaitAllPeers(canceller, handle) {
    const ret = wasm.claimerUserWaitAllPeers(canceller, handle);
    return ret;
}

/**
 * @param {number} client
 * @param {string} profile
 * @param {string} enrollment_id
 * @param {object} identity_strategy
 * @param {boolean} send_email
 * @returns {Promise<any>}
 */
export function clientAcceptAsyncEnrollment(client, profile, enrollment_id, identity_strategy, send_email) {
    const ptr0 = passStringToWasm0(profile, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(enrollment_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.clientAcceptAsyncEnrollment(client, ptr0, len0, ptr1, len1, identity_strategy, send_email);
    return ret;
}

/**
 * @param {number} client
 * @param {number} tos_updated_on
 * @returns {Promise<any>}
 */
export function clientAcceptTos(client, tos_updated_on) {
    const ret = wasm.clientAcceptTos(client, tos_updated_on);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @param {object} configuration
 * @returns {Promise<any>}
 */
export function clientArchiveWorkspace(client, realm_id, configuration) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientArchiveWorkspace(client, ptr0, len0, configuration);
    return ret;
}

/**
 * @param {number} client
 * @param {string} token
 * @returns {Promise<any>}
 */
export function clientCancelInvitation(client, token) {
    const ptr0 = passStringToWasm0(token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientCancelInvitation(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} name
 * @returns {Promise<any>}
 */
export function clientCreateWorkspace(client, name) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientCreateWorkspace(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client_handle
 * @returns {Promise<any>}
 */
export function clientDeleteShamirRecovery(client_handle) {
    const ret = wasm.clientDeleteShamirRecovery(client_handle);
    return ret;
}

/**
 * @param {number} client_handle
 * @param {string} device_label
 * @returns {Promise<any>}
 */
export function clientExportRecoveryDevice(client_handle, device_label) {
    const ptr0 = passStringToWasm0(device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientExportRecoveryDevice(client_handle, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientForgetAllCertificates(client) {
    const ret = wasm.clientForgetAllCertificates(client);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientGetAsyncEnrollmentAddr(client) {
    const ret = wasm.clientGetAsyncEnrollmentAddr(client);
    return ret;
}

/**
 * @param {number} client_handle
 * @returns {Promise<any>}
 */
export function clientGetOrganizationBootstrapDate(client_handle) {
    const ret = wasm.clientGetOrganizationBootstrapDate(client_handle);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientGetOutboundSyncBacklog(client) {
    const ret = wasm.clientGetOutboundSyncBacklog(client);
    return ret;
}

/**
 * @param {number} client_handle
 * @returns {Promise<any>}
 */
export function clientGetSelfShamirRecovery(client_handle) {
    const ret = wasm.clientGetSelfShamirRecovery(client_handle);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientGetTos(client) {
    const ret = wasm.clientGetTos(client);
    return ret;
}

/**
 * @param {number} client
 * @param {string} device
 * @returns {Promise<any>}
 */
export function clientGetUserDevice(client, device) {
    const ptr0 = passStringToWasm0(device, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientGetUserDevice(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} user_id
 * @returns {Promise<any>}
 */
export function clientGetUserInfo(client, user_id) {
    const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientGetUserInfo(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientInfo(client) {
    const ret = wasm.clientInfo(client);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientListAsyncEnrollments(client) {
    const ret = wasm.clientListAsyncEnrollments(client);
    return ret;
}

/**
 * @param {number} client_handle
 * @returns {Promise<any>}
 */
export function clientListFrozenUsers(client_handle) {
    const ret = wasm.clientListFrozenUsers(client_handle);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientListInvitations(client) {
    const ret = wasm.clientListInvitations(client);
    return ret;
}

/**
 * @param {number} client_handle
 * @returns {Promise<any>}
 */
export function clientListShamirRecoveriesForOthers(client_handle) {
    const ret = wasm.clientListShamirRecoveriesForOthers(client_handle);
    return ret;
}

/**
 * @param {number} client
 * @param {string} user
 * @returns {Promise<any>}
 */
export function clientListUserDevices(client, user) {
    const ptr0 = passStringToWasm0(user, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientListUserDevices(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {boolean} skip_revoked
 * @returns {Promise<any>}
 */
export function clientListUsers(client, skip_revoked) {
    const ret = wasm.clientListUsers(client, skip_revoked);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @returns {Promise<any>}
 */
export function clientListWorkspaceUsers(client, realm_id) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientListWorkspaceUsers(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientListWorkspaces(client) {
    const ret = wasm.clientListWorkspaces(client);
    return ret;
}

/**
 * @param {number} client
 * @param {boolean} send_email
 * @returns {Promise<any>}
 */
export function clientNewDeviceInvitation(client, send_email) {
    const ret = wasm.clientNewDeviceInvitation(client, send_email);
    return ret;
}

/**
 * @param {number} client
 * @param {string} claimer_user_id
 * @param {boolean} send_email
 * @returns {Promise<any>}
 */
export function clientNewShamirRecoveryInvitation(client, claimer_user_id, send_email) {
    const ptr0 = passStringToWasm0(claimer_user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientNewShamirRecoveryInvitation(client, ptr0, len0, send_email);
    return ret;
}

/**
 * @param {number} client
 * @param {string} claimer_email
 * @param {boolean} send_email
 * @returns {Promise<any>}
 */
export function clientNewUserInvitation(client, claimer_email, send_email) {
    const ptr0 = passStringToWasm0(claimer_email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientNewUserInvitation(client, ptr0, len0, send_email);
    return ret;
}

/**
 * @param {number} client_handle
 * @returns {Promise<any>}
 */
export function clientOrganizationInfo(client_handle) {
    const ret = wasm.clientOrganizationInfo(client_handle);
    return ret;
}

/**
 * @param {number} client
 * @param {string} enrollment_id
 * @returns {Promise<any>}
 */
export function clientRejectAsyncEnrollment(client, enrollment_id) {
    const ptr0 = passStringToWasm0(enrollment_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientRejectAsyncEnrollment(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @param {string} new_name
 * @returns {Promise<any>}
 */
export function clientRenameWorkspace(client, realm_id, new_name) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(new_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.clientRenameWorkspace(client, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {number} client
 * @param {string} user
 * @returns {Promise<any>}
 */
export function clientRevokeUser(client, user) {
    const ptr0 = passStringToWasm0(user, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientRevokeUser(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @returns {Promise<any>}
 */
export function clientSelfPromoteToWorkspaceOwner(client, realm_id) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientSelfPromoteToWorkspaceOwner(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client_handle
 * @param {object} per_recipient_shares
 * @param {number} threshold
 * @returns {Promise<any>}
 */
export function clientSetupShamirRecovery(client_handle, per_recipient_shares, threshold) {
    const ret = wasm.clientSetupShamirRecovery(client_handle, per_recipient_shares, threshold);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @param {string} recipient
 * @param {string | null} [role]
 * @returns {Promise<any>}
 */
export function clientShareWorkspace(client, realm_id, recipient, role) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(recipient, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(role) ? 0 : passStringToWasm0(role, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len2 = WASM_VECTOR_LEN;
    const ret = wasm.clientShareWorkspace(client, ptr0, len0, ptr1, len1, ptr2, len2);
    return ret;
}

/**
 * @param {object} config
 * @param {object} access
 * @returns {Promise<any>}
 */
export function clientStart(config, access) {
    const ret = wasm.clientStart(config, access);
    return ret;
}

/**
 * @param {number} client
 * @param {string} token
 * @returns {Promise<any>}
 */
export function clientStartDeviceInvitationGreet(client, token) {
    const ptr0 = passStringToWasm0(token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientStartDeviceInvitationGreet(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} token
 * @returns {Promise<any>}
 */
export function clientStartShamirRecoveryInvitationGreet(client, token) {
    const ptr0 = passStringToWasm0(token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientStartShamirRecoveryInvitationGreet(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} token
 * @returns {Promise<any>}
 */
export function clientStartUserInvitationGreet(client, token) {
    const ptr0 = passStringToWasm0(token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientStartUserInvitationGreet(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @returns {Promise<any>}
 */
export function clientStartWorkspace(client, realm_id) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientStartWorkspace(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @param {string} realm_id
 * @returns {Promise<any>}
 */
export function clientStartWorkspaceHistory(client, realm_id) {
    const ptr0 = passStringToWasm0(realm_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientStartWorkspaceHistory(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientStop(client) {
    const ret = wasm.clientStop(client);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientTotpCreateOpaqueKey(client) {
    const ret = wasm.clientTotpCreateOpaqueKey(client);
    return ret;
}

/**
 * @param {number} client
 * @param {string} one_time_password
 * @returns {Promise<any>}
 */
export function clientTotpSetupConfirm(client, one_time_password) {
    const ptr0 = passStringToWasm0(one_time_password, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.clientTotpSetupConfirm(client, ptr0, len0);
    return ret;
}

/**
 * @param {number} client
 * @returns {Promise<any>}
 */
export function clientTotpSetupStatus(client) {
    const ret = wasm.clientTotpSetupStatus(client);
    return ret;
}

/**
 * @param {number} client_handle
 * @param {string} user
 * @param {string} new_profile
 * @returns {Promise<any>}
 */
export function clientUpdateUserProfile(client_handle, user, new_profile) {
    const ptr0 = passStringToWasm0(user, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(new_profile, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.clientUpdateUserProfile(client_handle, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function getDefaultConfigDir() {
    const ret = wasm.getDefaultConfigDir();
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function getDefaultDataBaseDir() {
    const ret = wasm.getDefaultDataBaseDir();
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function getDefaultMountpointBaseDir() {
    const ret = wasm.getDefaultMountpointBaseDir();
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function getPlatform() {
    const ret = wasm.getPlatform();
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} addr
 * @returns {Promise<any>}
 */
export function getServerConfig(config_dir, addr) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.getServerConfig(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterDeviceInProgress1DoWaitPeerTrust(canceller, handle) {
    const ret = wasm.greeterDeviceInProgress1DoWaitPeerTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterDeviceInProgress2DoDenyTrust(canceller, handle) {
    const ret = wasm.greeterDeviceInProgress2DoDenyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterDeviceInProgress2DoSignifyTrust(canceller, handle) {
    const ret = wasm.greeterDeviceInProgress2DoSignifyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterDeviceInProgress3DoGetClaimRequests(canceller, handle) {
    const ret = wasm.greeterDeviceInProgress3DoGetClaimRequests(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @param {string} device_label
 * @returns {Promise<any>}
 */
export function greeterDeviceInProgress4DoCreate(canceller, handle, device_label) {
    const ptr0 = passStringToWasm0(device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.greeterDeviceInProgress4DoCreate(canceller, handle, ptr0, len0);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterDeviceInitialDoWaitPeer(canceller, handle) {
    const ret = wasm.greeterDeviceInitialDoWaitPeer(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterShamirRecoveryInProgress1DoWaitPeerTrust(canceller, handle) {
    const ret = wasm.greeterShamirRecoveryInProgress1DoWaitPeerTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterShamirRecoveryInProgress2DoDenyTrust(canceller, handle) {
    const ret = wasm.greeterShamirRecoveryInProgress2DoDenyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterShamirRecoveryInProgress2DoSignifyTrust(canceller, handle) {
    const ret = wasm.greeterShamirRecoveryInProgress2DoSignifyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterShamirRecoveryInProgress3DoGetClaimRequests(canceller, handle) {
    const ret = wasm.greeterShamirRecoveryInProgress3DoGetClaimRequests(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterShamirRecoveryInitialDoWaitPeer(canceller, handle) {
    const ret = wasm.greeterShamirRecoveryInitialDoWaitPeer(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterUserInProgress1DoWaitPeerTrust(canceller, handle) {
    const ret = wasm.greeterUserInProgress1DoWaitPeerTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterUserInProgress2DoDenyTrust(canceller, handle) {
    const ret = wasm.greeterUserInProgress2DoDenyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterUserInProgress2DoSignifyTrust(canceller, handle) {
    const ret = wasm.greeterUserInProgress2DoSignifyTrust(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterUserInProgress3DoGetClaimRequests(canceller, handle) {
    const ret = wasm.greeterUserInProgress3DoGetClaimRequests(canceller, handle);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @param {object} human_handle
 * @param {string} device_label
 * @param {string} profile
 * @returns {Promise<any>}
 */
export function greeterUserInProgress4DoCreate(canceller, handle, human_handle, device_label, profile) {
    const ptr0 = passStringToWasm0(device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(profile, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.greeterUserInProgress4DoCreate(canceller, handle, human_handle, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {number} canceller
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function greeterUserInitialDoWaitPeer(canceller, handle) {
    const ret = wasm.greeterUserInitialDoWaitPeer(canceller, handle);
    return ret;
}

/**
 * @param {object} config
 * @param {Uint8Array} recovery_device
 * @param {string} passphrase
 * @param {string} device_label
 * @param {object} save_strategy
 * @returns {Promise<any>}
 */
export function importRecoveryDevice(config, recovery_device, passphrase, device_label, save_strategy) {
    const ptr0 = passStringToWasm0(passphrase, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.importRecoveryDevice(config, recovery_device, ptr0, len0, ptr1, len1, save_strategy);
    return ret;
}

/**
 * @param {string} default_log_level
 */
export function initLogger(default_log_level) {
    wasm.initLogger(default_log_level);
}

/**
 * @returns {Promise<any>}
 */
export function isKeyringAvailable() {
    const ret = wasm.isKeyringAvailable();
    return ret;
}

/**
 * @param {number} client
 * @param {string} path
 * @returns {Promise<any>}
 */
export function isPathConfined(client, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isPathConfined(client, ptr0, len0);
    return ret;
}

/**
 * @param {object} config
 * @returns {Promise<any>}
 */
export function libparsecInitNativeOnlyInit(config) {
    const ret = wasm.libparsecInitNativeOnlyInit(config);
    return ret;
}

/**
 * @param {Function} on_event_callback
 * @returns {Promise<any>}
 */
export function libparsecInitSetOnEventCallback(on_event_callback) {
    const ret = wasm.libparsecInitSetOnEventCallback(on_event_callback);
    return ret;
}

/**
 * @param {string} path
 * @returns {Promise<any>}
 */
export function listAvailableDevices(path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.listAvailableDevices(ptr0, len0);
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function listStartedAccounts() {
    const ret = wasm.listStartedAccounts();
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function listStartedClients() {
    const ret = wasm.listStartedClients();
    return ret;
}

/**
 * @param {number} mountpoint
 * @param {string} parsec_path
 * @returns {Promise<any>}
 */
export function mountpointToOsPath(mountpoint, parsec_path) {
    const ptr0 = passStringToWasm0(parsec_path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.mountpointToOsPath(mountpoint, ptr0, len0);
    return ret;
}

/**
 * @param {number} mountpoint
 * @returns {Promise<any>}
 */
export function mountpointUnmount(mountpoint) {
    const ret = wasm.mountpointUnmount(mountpoint);
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function newCanceller() {
    const ret = wasm.newCanceller();
    return ret;
}

/**
 * @param {string} openbao_server_url
 * @param {string} openbao_secret_mount_path
 * @param {string} openbao_transit_mount_path
 * @param {string} openbao_entity_id
 * @param {string} openbao_auth_token
 * @returns {Promise<any>}
 */
export function openbaoListSelfEmails(openbao_server_url, openbao_secret_mount_path, openbao_transit_mount_path, openbao_entity_id, openbao_auth_token) {
    const ptr0 = passStringToWasm0(openbao_server_url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(openbao_secret_mount_path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(openbao_transit_mount_path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(openbao_entity_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passStringToWasm0(openbao_auth_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.openbaoListSelfEmails(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
    return ret;
}

/**
 * @param {string} url
 * @returns {Promise<any>}
 */
export function parseParsecAddr(url) {
    const ptr0 = passStringToWasm0(url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseParsecAddr(ptr0, len0);
    return ret;
}

/**
 * @param {string} path
 * @returns {Promise<any>}
 */
export function pathFilename(path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.pathFilename(ptr0, len0);
    return ret;
}

/**
 * @param {string} parent
 * @param {string} child
 * @returns {Promise<any>}
 */
export function pathJoin(parent, child) {
    const ptr0 = passStringToWasm0(parent, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(child, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.pathJoin(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {string} path
 * @returns {Promise<any>}
 */
export function pathNormalize(path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.pathNormalize(ptr0, len0);
    return ret;
}

/**
 * @param {string} path
 * @returns {Promise<any>}
 */
export function pathParent(path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.pathParent(ptr0, len0);
    return ret;
}

/**
 * @param {string} path
 * @returns {Promise<any>}
 */
export function pathSplit(path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.pathSplit(ptr0, len0);
    return ret;
}

/**
 * @param {string} config_dir
 * @returns {Promise<any>}
 */
export function pkiInitForNative(config_dir) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.pkiInitForNative(ptr0, len0);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} parsec_addr
 * @returns {Promise<any>}
 */
export function pkiInitForScws(config_dir, parsec_addr) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(parsec_addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.pkiInitForScws(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function pkiListUserCertificates() {
    const ret = wasm.pkiListUserCertificates();
    return ret;
}

/**
 * @param {object} cert_ref
 * @returns {Promise<any>}
 */
export function pkiOpenUserCertificatePrivateKey(cert_ref) {
    const ret = wasm.pkiOpenUserCertificatePrivateKey(cert_ref);
    return ret;
}

/**
 * @param {number} handle
 * @returns {Promise<any>}
 */
export function pkiPrivateKeyClose(handle) {
    const ret = wasm.pkiPrivateKeyClose(handle);
    return ret;
}

/**
 * @param {object} config
 * @param {string} device_id
 * @returns {Promise<any>}
 */
export function removeDeviceData(config, device_id) {
    const ptr0 = passStringToWasm0(device_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.removeDeviceData(config, ptr0, len0);
    return ret;
}

/**
 * @returns {Promise<any>}
 */
export function showCertificateSelectionDialogWindowsOnly() {
    const ret = wasm.showCertificateSelectionDialogWindowsOnly();
    return ret;
}

/**
 * @param {object} config
 * @param {string} addr
 * @param {boolean} force
 * @param {string} requested_device_label
 * @param {object} identity_strategy
 * @returns {Promise<any>}
 */
export function submitAsyncEnrollment(config, addr, force, requested_device_label, identity_strategy) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(requested_device_label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.submitAsyncEnrollment(config, ptr0, len0, force, ptr1, len1, identity_strategy);
    return ret;
}

/**
 * @param {object} config
 * @param {string} addr
 * @param {string} enrollment_id
 * @returns {Promise<any>}
 */
export function submitterCancelAsyncEnrollment(config, addr, enrollment_id) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(enrollment_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.submitterCancelAsyncEnrollment(config, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {object} config
 * @param {string} enrollment_file
 * @param {object} new_device_save_strategy
 * @param {object} identity_strategy
 * @returns {Promise<any>}
 */
export function submitterFinalizeAsyncEnrollment(config, enrollment_file, new_device_save_strategy, identity_strategy) {
    const ptr0 = passStringToWasm0(enrollment_file, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.submitterFinalizeAsyncEnrollment(config, ptr0, len0, new_device_save_strategy, identity_strategy);
    return ret;
}

/**
 * @param {object} config
 * @param {string} addr
 * @param {string} enrollment_id
 * @returns {Promise<any>}
 */
export function submitterGetAsyncEnrollmentInfo(config, addr, enrollment_id) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(enrollment_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.submitterGetAsyncEnrollmentInfo(config, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {string} config_dir
 * @returns {Promise<any>}
 */
export function submitterListAsyncEnrollments(config_dir) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.submitterListAsyncEnrollments(ptr0, len0);
    return ret;
}

/**
 * @param {string} server_addr
 * @param {string} email
 * @returns {Promise<any>}
 */
export function testCheckMailbox(server_addr, email) {
    const ptr0 = passStringToWasm0(server_addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(email, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.testCheckMailbox(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {string} path
 * @returns {Promise<any>}
 */
export function testDropTestbed(path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.testDropTestbed(ptr0, len0);
    return ret;
}

/**
 * @param {string} discriminant_dir
 * @returns {Promise<any>}
 */
export function testGetTestbedBootstrapOrganizationAddr(discriminant_dir) {
    const ptr0 = passStringToWasm0(discriminant_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.testGetTestbedBootstrapOrganizationAddr(ptr0, len0);
    return ret;
}

/**
 * @param {string} discriminant_dir
 * @returns {Promise<any>}
 */
export function testGetTestbedOrganizationId(discriminant_dir) {
    const ptr0 = passStringToWasm0(discriminant_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.testGetTestbedOrganizationId(ptr0, len0);
    return ret;
}

/**
 * @param {string} server_addr
 * @returns {Promise<any>}
 */
export function testNewAccount(server_addr) {
    const ptr0 = passStringToWasm0(server_addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.testNewAccount(ptr0, len0);
    return ret;
}

/**
 * @param {string} template
 * @param {string | null} [test_server]
 * @returns {Promise<any>}
 */
export function testNewTestbed(template, test_server) {
    const ptr0 = passStringToWasm0(template, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    var ptr1 = isLikeNone(test_server) ? 0 : passStringToWasm0(test_server, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    const ret = wasm.testNewTestbed(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {object} config
 * @param {string} server_addr
 * @param {string} organization_id
 * @param {string} user_id
 * @param {string} opaque_key_id
 * @param {string} one_time_password
 * @returns {Promise<any>}
 */
export function totpFetchOpaqueKey(config, server_addr, organization_id, user_id, opaque_key_id, one_time_password) {
    const ptr0 = passStringToWasm0(server_addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(organization_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(opaque_key_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passStringToWasm0(one_time_password, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.totpFetchOpaqueKey(config, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
    return ret;
}

/**
 * @param {object} config
 * @param {string} addr
 * @param {string} one_time_password
 * @returns {Promise<any>}
 */
export function totpSetupConfirmAnonymous(config, addr, one_time_password) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(one_time_password, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.totpSetupConfirmAnonymous(config, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {object} config
 * @param {string} addr
 * @returns {Promise<any>}
 */
export function totpSetupStatusAnonymous(config, addr) {
    const ptr0 = passStringToWasm0(addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.totpSetupStatusAnonymous(config, ptr0, len0);
    return ret;
}

/**
 * @param {string} http_url
 * @returns {Promise<any>}
 */
export function tryConvertHttpToParsecAddr(http_url) {
    const ptr0 = passStringToWasm0(http_url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.tryConvertHttpToParsecAddr(ptr0, len0);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {object} current_auth
 * @param {object} new_auth
 * @returns {Promise<any>}
 */
export function updateDeviceChangeAuthentication(config_dir, current_auth, new_auth) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.updateDeviceChangeAuthentication(ptr0, len0, current_auth, new_auth);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {object} access
 * @param {string} new_server_addr
 * @returns {Promise<any>}
 */
export function updateDeviceOverwriteServerAddr(config_dir, access, new_server_addr) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(new_server_addr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.updateDeviceOverwriteServerAddr(ptr0, len0, access, ptr1, len1);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validateDeviceLabel(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validateDeviceLabel(ptr0, len0);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validateEmail(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validateEmail(ptr0, len0);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validateEntryName(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validateEntryName(ptr0, len0);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validateHumanHandleLabel(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validateHumanHandleLabel(ptr0, len0);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validateInvitationToken(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validateInvitationToken(ptr0, len0);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validateOrganizationId(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validateOrganizationId(ptr0, len0);
    return ret;
}

/**
 * @param {string} raw
 * @returns {Promise<any>}
 */
export function validatePath(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.validatePath(ptr0, len0);
    return ret;
}

/**
 * @param {string} config_dir
 * @param {string} device_id
 * @returns {Promise<any>}
 */
export function waitForDeviceAvailable(config_dir, device_id) {
    const ptr0 = passStringToWasm0(config_dir, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(device_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.waitForDeviceAvailable(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceCreateFile(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceCreateFile(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceCreateFolder(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceCreateFolder(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceCreateFolderAll(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceCreateFolderAll(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} link
 * @returns {Promise<any>}
 */
export function workspaceDecryptPathAddr(workspace, link) {
    const ptr0 = passStringToWasm0(link, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceDecryptPathAddr(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @returns {Promise<any>}
 */
export function workspaceFdClose(workspace, fd) {
    const ret = wasm.workspaceFdClose(workspace, fd);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @returns {Promise<any>}
 */
export function workspaceFdFlush(workspace, fd) {
    const ret = wasm.workspaceFdFlush(workspace, fd);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @param {bigint} offset
 * @param {bigint} size
 * @returns {Promise<any>}
 */
export function workspaceFdRead(workspace, fd, offset, size) {
    const ret = wasm.workspaceFdRead(workspace, fd, offset, size);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @param {bigint} length
 * @param {boolean} truncate_only
 * @returns {Promise<any>}
 */
export function workspaceFdResize(workspace, fd, length, truncate_only) {
    const ret = wasm.workspaceFdResize(workspace, fd, length, truncate_only);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @returns {Promise<any>}
 */
export function workspaceFdStat(workspace, fd) {
    const ret = wasm.workspaceFdStat(workspace, fd);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @param {bigint} offset
 * @param {Uint8Array} data
 * @returns {Promise<any>}
 */
export function workspaceFdWrite(workspace, fd, offset, data) {
    const ret = wasm.workspaceFdWrite(workspace, fd, offset, data);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @param {bigint} offset
 * @param {Uint8Array} data
 * @returns {Promise<any>}
 */
export function workspaceFdWriteConstrainedIo(workspace, fd, offset, data) {
    const ret = wasm.workspaceFdWriteConstrainedIo(workspace, fd, offset, data);
    return ret;
}

/**
 * @param {number} workspace
 * @param {bigint} fd
 * @param {Uint8Array} data
 * @returns {Promise<any>}
 */
export function workspaceFdWriteStartEof(workspace, fd, data) {
    const ret = wasm.workspaceFdWriteStartEof(workspace, fd, data);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceGeneratePathAddr(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceGeneratePathAddr(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {bigint} fd
 * @returns {Promise<any>}
 */
export function workspaceHistoryFdClose(workspace_history, fd) {
    const ret = wasm.workspaceHistoryFdClose(workspace_history, fd);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {bigint} fd
 * @param {bigint} offset
 * @param {bigint} size
 * @returns {Promise<any>}
 */
export function workspaceHistoryFdRead(workspace_history, fd, offset, size) {
    const ret = wasm.workspaceHistoryFdRead(workspace_history, fd, offset, size);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {bigint} fd
 * @returns {Promise<any>}
 */
export function workspaceHistoryFdStat(workspace_history, fd) {
    const ret = wasm.workspaceHistoryFdStat(workspace_history, fd);
    return ret;
}

/**
 * @param {number} workspace_history
 * @returns {Promise<any>}
 */
export function workspaceHistoryGetTimestampHigherBound(workspace_history) {
    const ret = wasm.workspaceHistoryGetTimestampHigherBound(workspace_history);
    return ret;
}

/**
 * @param {number} workspace_history
 * @returns {Promise<any>}
 */
export function workspaceHistoryGetTimestampLowerBound(workspace_history) {
    const ret = wasm.workspaceHistoryGetTimestampLowerBound(workspace_history);
    return ret;
}

/**
 * @param {number} workspace_history
 * @returns {Promise<any>}
 */
export function workspaceHistoryGetTimestampOfInterest(workspace_history) {
    const ret = wasm.workspaceHistoryGetTimestampOfInterest(workspace_history);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceHistoryOpenFile(workspace_history, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryOpenFile(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceHistoryOpenFileAndGetId(workspace_history, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryOpenFileAndGetId(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} entry_id
 * @returns {Promise<any>}
 */
export function workspaceHistoryOpenFileById(workspace_history, entry_id) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryOpenFileById(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {number} toi
 * @returns {Promise<any>}
 */
export function workspaceHistorySetTimestampOfInterest(workspace_history, toi) {
    const ret = wasm.workspaceHistorySetTimestampOfInterest(workspace_history, toi);
    return ret;
}

/**
 * @param {object} config
 * @param {string} export_db_path
 * @param {object[]} decryptors
 * @returns {Promise<any>}
 */
export function workspaceHistoryStartWithRealmExport(config, export_db_path, decryptors) {
    const ptr0 = passStringToWasm0(export_db_path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(decryptors, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryStartWithRealmExport(config, ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceHistoryStatEntry(workspace_history, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryStatEntry(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} entry_id
 * @returns {Promise<any>}
 */
export function workspaceHistoryStatEntryById(workspace_history, entry_id) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryStatEntryById(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceHistoryStatFolderChildren(workspace_history, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryStatFolderChildren(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @param {string} entry_id
 * @returns {Promise<any>}
 */
export function workspaceHistoryStatFolderChildrenById(workspace_history, entry_id) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceHistoryStatFolderChildrenById(workspace_history, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace_history
 * @returns {Promise<any>}
 */
export function workspaceHistoryStop(workspace_history) {
    const ret = wasm.workspaceHistoryStop(workspace_history);
    return ret;
}

/**
 * @param {number} workspace
 * @returns {Promise<any>}
 */
export function workspaceInfo(workspace) {
    const ret = wasm.workspaceInfo(workspace);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceIsFileContentLocal(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceIsFileContentLocal(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @returns {Promise<any>}
 */
export function workspaceMount(workspace) {
    const ret = wasm.workspaceMount(workspace);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} src
 * @param {string} dst
 * @param {object} mode
 * @returns {Promise<any>}
 */
export function workspaceMoveEntry(workspace, src, dst, mode) {
    const ptr0 = passStringToWasm0(src, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(dst, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceMoveEntry(workspace, ptr0, len0, ptr1, len1, mode);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @param {object} mode
 * @returns {Promise<any>}
 */
export function workspaceOpenFile(workspace, path, mode) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceOpenFile(workspace, ptr0, len0, mode);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @param {object} mode
 * @returns {Promise<any>}
 */
export function workspaceOpenFileAndGetId(workspace, path, mode) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceOpenFileAndGetId(workspace, ptr0, len0, mode);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} entry_id
 * @param {object} mode
 * @returns {Promise<any>}
 */
export function workspaceOpenFileById(workspace, entry_id, mode) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceOpenFileById(workspace, ptr0, len0, mode);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} vlob_id
 * @param {string} candidate_view_key
 * @param {string} candidate_edit_key
 * @returns {Promise<any>}
 */
export function workspaceRegisterCryptpadSession(workspace, vlob_id, candidate_view_key, candidate_edit_key) {
    const ptr0 = passStringToWasm0(vlob_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(candidate_view_key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(candidate_edit_key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceRegisterCryptpadSession(workspace, ptr0, len0, ptr1, len1, ptr2, len2);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceRemoveEntry(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceRemoveEntry(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceRemoveFile(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceRemoveFile(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceRemoveFolder(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceRemoveFolder(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceRemoveFolderAll(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceRemoveFolderAll(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} src_parent_id
 * @param {string} src_name
 * @param {string} dst_name
 * @param {object} mode
 * @returns {Promise<any>}
 */
export function workspaceRenameEntryById(workspace, src_parent_id, src_name, dst_name, mode) {
    const ptr0 = passStringToWasm0(src_parent_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(src_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(dst_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceRenameEntryById(workspace, ptr0, len0, ptr1, len1, ptr2, len2, mode);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} entry_id
 * @param {string} cryptpad_session_channel_id
 * @param {number} cryptpad_session_timestamp
 * @param {Uint8Array} content
 * @returns {Promise<any>}
 */
export function workspaceSaveAndSyncFileWithCryptpad(workspace, entry_id, cryptpad_session_channel_id, cryptpad_session_timestamp, content) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(cryptpad_session_channel_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceSaveAndSyncFileWithCryptpad(workspace, ptr0, len0, ptr1, len1, cryptpad_session_timestamp, content);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceStatEntry(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceStatEntry(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} entry_id
 * @returns {Promise<any>}
 */
export function workspaceStatEntryById(workspace, entry_id) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceStatEntryById(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} entry_id
 * @returns {Promise<any>}
 */
export function workspaceStatEntryByIdIgnoreConfinementPoint(workspace, entry_id) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceStatEntryByIdIgnoreConfinementPoint(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceStatFolderChildren(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceStatFolderChildren(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} entry_id
 * @returns {Promise<any>}
 */
export function workspaceStatFolderChildrenById(workspace, entry_id) {
    const ptr0 = passStringToWasm0(entry_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceStatFolderChildrenById(workspace, ptr0, len0);
    return ret;
}

/**
 * @param {number} workspace
 * @returns {Promise<any>}
 */
export function workspaceStop(workspace) {
    const ret = wasm.workspaceStop(workspace);
    return ret;
}

/**
 * @param {number} workspace
 * @param {string} path
 * @returns {Promise<any>}
 */
export function workspaceWatchEntryOneshot(workspace, path) {
    const ptr0 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.workspaceWatchEntryOneshot(workspace, ptr0, len0);
    return ret;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_960c155d3d49e4c2: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_String_8564e559799eccda: function(arg0, arg1) {
            const ret = String(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_boolean_get_6ea149f0a8dcc5ff: function(arg0) {
            const v = arg0;
            const ret = typeof(v) === 'boolean' ? v : undefined;
            return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
        },
        __wbg___wbindgen_debug_string_ab4b34d23d6778bd: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_is_function_3baa9db1a987f47d: function(arg0) {
            const ret = typeof(arg0) === 'function';
            return ret;
        },
        __wbg___wbindgen_is_null_52ff4ec04186736f: function(arg0) {
            const ret = arg0 === null;
            return ret;
        },
        __wbg___wbindgen_is_object_63322ec0cd6ea4ef: function(arg0) {
            const val = arg0;
            const ret = typeof(val) === 'object' && val !== null;
            return ret;
        },
        __wbg___wbindgen_is_string_6df3bf7ef1164ed3: function(arg0) {
            const ret = typeof(arg0) === 'string';
            return ret;
        },
        __wbg___wbindgen_is_undefined_29a43b4d42920abd: function(arg0) {
            const ret = arg0 === undefined;
            return ret;
        },
        __wbg___wbindgen_jsval_eq_d3465d8a07697228: function(arg0, arg1) {
            const ret = arg0 === arg1;
            return ret;
        },
        __wbg___wbindgen_number_get_c7f42aed0525c451: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'number' ? obj : undefined;
            getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_string_get_7ed5322991caaec5: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'string' ? obj : undefined;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_6b64449b9b9ed33c: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg__wbg_cb_unref_b46c9b5a9f08ec37: function(arg0) {
            arg0._wbg_cb_unref();
        },
        __wbg_abort_4ce5b484434ef6fd: function(arg0) {
            arg0.abort();
        },
        __wbg_abort_b64b5c37b7b5156d: function() { return handleError(function (arg0) {
            arg0.abort();
        }, arguments); },
        __wbg_abort_d53712380a54cc81: function(arg0, arg1) {
            arg0.abort(arg1);
        },
        __wbg_add_e04fd556bc38ef11: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.add(arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_advance_cb186a77f04d7228: function() { return handleError(function (arg0, arg1) {
            arg0.advance(arg1 >>> 0);
        }, arguments); },
        __wbg_append_e8fc56ce7c00e874: function() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
            arg0.append(getStringFromWasm0(arg1, arg2), getStringFromWasm0(arg3, arg4));
        }, arguments); },
        __wbg_arrayBuffer_473644614d8643a5: function(arg0) {
            const ret = arg0.arrayBuffer();
            return ret;
        },
        __wbg_arrayBuffer_848c392b70c67d3d: function() { return handleError(function (arg0) {
            const ret = arg0.arrayBuffer();
            return ret;
        }, arguments); },
        __wbg_body_0c3a51aec038a31a: function(arg0) {
            const ret = arg0.body;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_bound_d976f60d9eb7b26b: function() { return handleError(function (arg0, arg1, arg2, arg3) {
            const ret = IDBKeyRange.bound(arg0, arg1, arg2 !== 0, arg3 !== 0);
            return ret;
        }, arguments); },
        __wbg_buffer_d0f5ea0926a691fd: function(arg0) {
            const ret = arg0.buffer;
            return ret;
        },
        __wbg_byobRequest_dc6aed9db01b12c6: function(arg0) {
            const ret = arg0.byobRequest;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_byteLength_3e660e5661f3327e: function(arg0) {
            const ret = arg0.byteLength;
            return ret;
        },
        __wbg_byteOffset_ecd62abe44dd28d4: function(arg0) {
            const ret = arg0.byteOffset;
            return ret;
        },
        __wbg_call_a24592a6f349a97e: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.call(arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_call_bb28efe6b2f55b86: function() { return handleError(function (arg0, arg1, arg2, arg3) {
            const ret = arg0.call(arg1, arg2, arg3);
            return ret;
        }, arguments); },
        __wbg_cancel_ceb1bda02e29f0a9: function(arg0) {
            const ret = arg0.cancel();
            return ret;
        },
        __wbg_catch_e9362815fd0b24cf: function(arg0, arg1) {
            const ret = arg0.catch(arg1);
            return ret;
        },
        __wbg_certPath_564cfc1a977eed9e: function(arg0, arg1) {
            const ret = arg1.certPath;
            const ptr1 = passArrayJsValueToWasm0(ret, wasm.__wbindgen_malloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_challenge_363f0c32dc087db1: function(arg0) {
            const ret = arg0.challenge;
            return ret;
        },
        __wbg_ckId_01bb610e26315a4a: function(arg0, arg1) {
            const ret = arg1.ckId;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_ckId_370fc5ecd6955ecc: function(arg0, arg1) {
            const ret = arg1.ckId;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_ckId_dca7ab10a234c92a: function(arg0, arg1) {
            const ret = arg1.ckId;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_ckLabel_2ae8fac27c753150: function(arg0, arg1) {
            const ret = arg1.ckLabel;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_clearTimeout_1daad5c6ee4fcd3b: function(arg0) {
            const ret = clearTimeout(arg0);
            return ret;
        },
        __wbg_clearTimeout_3629d6209dfcc46e: function(arg0) {
            const ret = clearTimeout(arg0);
            return ret;
        },
        __wbg_clear_e20ff80e7eda7707: function() { return handleError(function (arg0) {
            const ret = arg0.clear();
            return ret;
        }, arguments); },
        __wbg_close_a7d2c11ae747c112: function(arg0) {
            arg0.close();
        },
        __wbg_close_e6c8977a002e9e13: function() { return handleError(function (arg0) {
            arg0.close();
        }, arguments); },
        __wbg_close_f1f785ddcbd2603d: function(arg0) {
            const ret = arg0.close();
            return ret;
        },
        __wbg_close_fb954dfaf67b5732: function() { return handleError(function (arg0) {
            arg0.close();
        }, arguments); },
        __wbg_code_e2d14bb68011f972: function(arg0) {
            const ret = arg0.code;
            return ret;
        },
        __wbg_connect_d7f3ac95f49442d0: function() { return handleError(function (arg0) {
            const ret = arg0.connect();
            return ret;
        }, arguments); },
        __wbg_count_9fc90fc071a17693: function() { return handleError(function (arg0) {
            const ret = arg0.count();
            return ret;
        }, arguments); },
        __wbg_createEnvironment_4fa8a40004fbbb5e: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.createEnvironment(getStringFromWasm0(arg1, arg2));
            return ret;
        }, arguments); },
        __wbg_createIndex_b7dc3a4f19fc4231: function() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
            const ret = arg0.createIndex(getStringFromWasm0(arg1, arg2), arg3, arg4);
            return ret;
        }, arguments); },
        __wbg_createObjectStore_9cf3048eb7b47bc2: function() { return handleError(function (arg0, arg1, arg2, arg3) {
            const ret = arg0.createObjectStore(getStringFromWasm0(arg1, arg2), arg3);
            return ret;
        }, arguments); },
        __wbg_createWritable_2adbc5040005623d: function(arg0) {
            const ret = arg0.createWritable();
            return ret;
        },
        __wbg_crypto_48300657fced39f9: function(arg0) {
            const ret = arg0.crypto;
            return ret;
        },
        __wbg_cryptogram_384f77228dbf5eae: function(arg0) {
            const ret = arg0.cryptogram;
            return ret;
        },
        __wbg_debug_c014a160490283dc: function(arg0) {
            console.debug(arg0);
        },
        __wbg_decrypt_62263f126b7a3142: function() { return handleError(function (arg0, arg1, arg2, arg3) {
            const ret = arg0.decrypt(getArrayU8FromWasm0(arg1, arg2), arg3);
            return ret;
        }, arguments); },
        __wbg_deleteDatabase_a42aa58a976e0c6f: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.deleteDatabase(getStringFromWasm0(arg1, arg2));
            return ret;
        }, arguments); },
        __wbg_delete_10b3489d089185d7: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.delete(arg1);
            return ret;
        }, arguments); },
        __wbg_delete_be787f4f73f54f72: function() { return handleError(function (arg0) {
            const ret = arg0.delete();
            return ret;
        }, arguments); },
        __wbg_done_9158f7cc8751ba32: function(arg0) {
            const ret = arg0.done;
            return ret;
        },
        __wbg_enqueue_4767ce322820c94d: function() { return handleError(function (arg0, arg1) {
            arg0.enqueue(arg1);
        }, arguments); },
        __wbg_entries_bf727fcd7bf35a41: function(arg0) {
            const ret = arg0.entries();
            return ret;
        },
        __wbg_error_2001591ad2463697: function(arg0) {
            console.error(arg0);
        },
        __wbg_error_239e7ebdfcec8d55: function(arg0, arg1) {
            let deferred0_0;
            let deferred0_1;
            try {
                deferred0_0 = arg0;
                deferred0_1 = arg1;
                console.error(getStringFromWasm0(arg0, arg1));
            } finally {
                wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
            }
        },
        __wbg_error_43690a185e0311ac: function() { return handleError(function (arg0) {
            const ret = arg0.error;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        }, arguments); },
        __wbg_fetch_0d322c0aed196b8b: function(arg0, arg1) {
            const ret = arg0.fetch(arg1);
            return ret;
        },
        __wbg_fetch_7a37768ab67c5bd8: function(arg0) {
            const ret = fetch(arg0);
            return ret;
        },
        __wbg_findService_5fff635647e21435: function() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
            const ret = arg0.findService(getStringFromWasm0(arg1, arg2), getStringFromWasm0(arg3, arg4));
            return ret;
        }, arguments); },
        __wbg_from_0dbf29f09e7fb200: function(arg0) {
            const ret = Array.from(arg0);
            return ret;
        },
        __wbg_getDirectoryHandle_91688a7455a56958: function(arg0, arg1, arg2, arg3) {
            const ret = arg0.getDirectoryHandle(getStringFromWasm0(arg1, arg2), arg3);
            return ret;
        },
        __wbg_getDirectoryHandle_aeab09d28f24c451: function(arg0, arg1, arg2) {
            const ret = arg0.getDirectoryHandle(getStringFromWasm0(arg1, arg2));
            return ret;
        },
        __wbg_getDirectory_3b3e1fae9108e816: function(arg0) {
            const ret = arg0.getDirectory();
            return ret;
        },
        __wbg_getFileHandle_a930fb20c238e32d: function(arg0, arg1, arg2) {
            const ret = arg0.getFileHandle(getStringFromWasm0(arg1, arg2));
            return ret;
        },
        __wbg_getFileHandle_c70a158776f61068: function(arg0, arg1, arg2, arg3) {
            const ret = arg0.getFileHandle(getStringFromWasm0(arg1, arg2), arg3);
            return ret;
        },
        __wbg_getFile_0f5a0aed86383786: function(arg0) {
            const ret = arg0.getFile();
            return ret;
        },
        __wbg_getObjects_eed8f55e3bf02f87: function(arg0) {
            const ret = arg0.getObjects();
            return ret;
        },
        __wbg_getRandomValues_263d0aa5464054ee: function() { return handleError(function (arg0, arg1) {
            arg0.getRandomValues(arg1);
        }, arguments); },
        __wbg_getRandomValues_6c1f5ee6d7758e5e: function() { return handleError(function (arg0, arg1) {
            globalThis.crypto.getRandomValues(getArrayU8FromWasm0(arg0, arg1));
        }, arguments); },
        __wbg_getReader_9facd4f899beac89: function() { return handleError(function (arg0) {
            const ret = arg0.getReader();
            return ret;
        }, arguments); },
        __wbg_getTime_da7c55f52b71e8c6: function(arg0) {
            const ret = arg0.getTime();
            return ret;
        },
        __wbg_getTrust_9aca3f5e122ca80a: function() { return handleError(function (arg0) {
            const ret = arg0.getTrust();
            return ret;
        }, arguments); },
        __wbg_getValue_1e10dd66d72486a9: function(arg0) {
            const ret = arg0.getValue();
            return ret;
        },
        __wbg_get_6011fa3a58f61074: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_7a3ccde226c74000: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1 >>> 0);
            return ret;
        }, arguments); },
        __wbg_get_803cfa78503173c3: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.get(arg1);
            return ret;
        }, arguments); },
        __wbg_get_8360291721e2339f: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_done_282bca5d3f90e0a8: function(arg0) {
            const ret = arg0.done;
            return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
        },
        __wbg_get_unchecked_17f53dad852b9588: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_value_65a7a2c60b42fd75: function(arg0) {
            const ret = arg0.value;
            return ret;
        },
        __wbg_has_880f1d472f7cecba: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.has(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_headers_6022deb4e576fb8e: function(arg0) {
            const ret = arg0.headers;
            return ret;
        },
        __wbg_includes_591176a7a8b346e9: function(arg0, arg1, arg2) {
            const ret = arg0.includes(arg1, arg2);
            return ret;
        },
        __wbg_index_072be17bcbd46242: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.index(getStringFromWasm0(arg1, arg2));
            return ret;
        }, arguments); },
        __wbg_indexedDB_9cd90e32ea3da2e4: function() { return handleError(function (arg0) {
            const ret = arg0.indexedDB;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        }, arguments); },
        __wbg_indexedDB_ac4afadd7c87762a: function() { return handleError(function (arg0) {
            const ret = arg0.indexedDB;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        }, arguments); },
        __wbg_info_7479429238bffbce: function(arg0) {
            console.info(arg0);
        },
        __wbg_instanceof_ArrayBuffer_7c8433c6ed14ffe3: function(arg0) {
            let result;
            try {
                result = arg0 instanceof ArrayBuffer;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_DomException_2ce86bc775566ba2: function(arg0) {
            let result;
            try {
                result = arg0 instanceof DOMException;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_FileSystemDirectoryHandle_b94ae0051739fba7: function(arg0) {
            let result;
            try {
                result = arg0 instanceof FileSystemDirectoryHandle;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_FileSystemFileHandle_37049c6f02bdc571: function(arg0) {
            let result;
            try {
                result = arg0 instanceof FileSystemFileHandle;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_FileSystemWritableFileStream_faaba7ece2d95823: function(arg0) {
            let result;
            try {
                result = arg0 instanceof FileSystemWritableFileStream;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_File_2967c9e9f297f988: function(arg0) {
            let result;
            try {
                result = arg0 instanceof File;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_IdbCursorWithValue_5831aaa3200efb22: function(arg0) {
            let result;
            try {
                result = arg0 instanceof IDBCursorWithValue;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_IdbCursor_98630480e23ef2a1: function(arg0) {
            let result;
            try {
                result = arg0 instanceof IDBCursor;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_IdbDatabase_3b6930bc25a91170: function(arg0) {
            let result;
            try {
                result = arg0 instanceof IDBDatabase;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_IdbOpenDbRequest_d0071b8c6aae51ff: function(arg0) {
            let result;
            try {
                result = arg0 instanceof IDBOpenDBRequest;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_IdbRequest_cb20b218916840b2: function(arg0) {
            let result;
            try {
                result = arg0 instanceof IDBRequest;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Map_1b76fd4635be43eb: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Map;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Object_7c99480a1cdfb911: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Object;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Response_9b2d111407865ff2: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Response;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_TypeError_2d6bc8d54f323e4b: function(arg0) {
            let result;
            try {
                result = arg0 instanceof TypeError;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Uint8Array_152ba1f289edcf3f: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Uint8Array;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Window_cc64c86c8ef9e02b: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Window;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_WorkerGlobalScope_47026c798529a771: function(arg0) {
            let result;
            try {
                result = arg0 instanceof WorkerGlobalScope;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_isArray_c3109d14ffc06469: function(arg0) {
            const ret = Array.isArray(arg0);
            return ret;
        },
        __wbg_isInteger_bc636508dda04ce7: function(arg0) {
            const ret = Number.isInteger(arg0);
            return ret;
        },
        __wbg_keyID_2de1f6f117482eef: function(arg0) {
            const ret = arg0.keyID;
            return ret;
        },
        __wbg_key_3b366eba0fc3946a: function() { return handleError(function (arg0) {
            const ret = arg0.key;
            return ret;
        }, arguments); },
        __wbg_keys_7fc58fd5f4899356: function(arg0) {
            const ret = arg0.keys();
            return ret;
        },
        __wbg_label_fc48a67ef31d45c0: function(arg0, arg1) {
            const ret = arg1.label;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_length_3d4ecd04bd8d22f1: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_length_9f1775224cf1d815: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_log_7e1aa9064a1dbdbd: function(arg0) {
            console.log(arg0);
        },
        __wbg_login_50bf2a21205eaf87: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.login(arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_msCrypto_8c6d45a75ef1d3da: function(arg0) {
            const ret = arg0.msCrypto;
            return ret;
        },
        __wbg_name_9e40a3f1f5a3e2e4: function(arg0, arg1) {
            const ret = arg1.name;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_name_f7d813fd122b04af: function(arg0, arg1) {
            const ret = arg1.name;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_name_fce2a531eb1066ad: function(arg0, arg1) {
            const ret = arg1.name;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_navigator_353318de944ca7f6: function(arg0) {
            const ret = arg0.navigator;
            return ret;
        },
        __wbg_navigator_bc077756492232c5: function(arg0) {
            const ret = arg0.navigator;
            return ret;
        },
        __wbg_new_0_4d657201ced14de3: function() {
            const ret = new Date();
            return ret;
        },
        __wbg_new_0c7403db6e782f19: function(arg0) {
            const ret = new Uint8Array(arg0);
            return ret;
        },
        __wbg_new_15a4889b4b90734d: function() { return handleError(function () {
            const ret = new Headers();
            return ret;
        }, arguments); },
        __wbg_new_191521fecb171639: function(arg0, arg1) {
            const ret = new RangeError(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_24a82d8799c298c3: function(arg0, arg1) {
            const ret = new TypeError(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_34d45cc8e36aaead: function() {
            const ret = new Map();
            return ret;
        },
        __wbg_new_5e360d2ff7b9e1c3: function(arg0, arg1) {
            const ret = new Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_88d73322198219db: function() {
            const ret = new Error();
            return ret;
        },
        __wbg_new_98c22165a42231aa: function() { return handleError(function () {
            const ret = new AbortController();
            return ret;
        }, arguments); },
        __wbg_new_aa8d0fa9762c29bd: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_new_from_slice_b5ea43e23f6008c0: function(arg0, arg1) {
            const ret = new Uint8Array(getArrayU8FromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_typed_323f37fd55ab048d: function(arg0, arg1) {
            try {
                var state0 = {a: arg0, b: arg1};
                var cb0 = (arg0, arg1) => {
                    const a = state0.a;
                    state0.a = 0;
                    try {
                        return wasm_bindgen__convert__closures_____invoke__h882fceb916efd94b(a, state0.b, arg0, arg1);
                    } finally {
                        state0.a = a;
                    }
                };
                const ret = new Promise(cb0);
                return ret;
            } finally {
                state0.a = 0;
            }
        },
        __wbg_new_with_byte_offset_and_length_01848e8d6a3d49ad: function(arg0, arg1, arg2) {
            const ret = new Uint8Array(arg0, arg1 >>> 0, arg2 >>> 0);
            return ret;
        },
        __wbg_new_with_length_223c4ea248649e55: function(arg0) {
            const ret = new Array(arg0 >>> 0);
            return ret;
        },
        __wbg_new_with_length_8c854e41ea4dae9b: function(arg0) {
            const ret = new Uint8Array(arg0 >>> 0);
            return ret;
        },
        __wbg_new_with_str_and_init_897be1708e42f39d: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = new Request(getStringFromWasm0(arg0, arg1), arg2);
            return ret;
        }, arguments); },
        __wbg_next_0340c4ae324393c3: function() { return handleError(function (arg0) {
            const ret = arg0.next();
            return ret;
        }, arguments); },
        __wbg_next_36ce9804d16f8eb4: function() { return handleError(function (arg0) {
            const ret = arg0.next();
            return ret;
        }, arguments); },
        __wbg_node_95beb7570492fd97: function(arg0) {
            const ret = arg0.node;
            return ret;
        },
        __wbg_objectStore_5942e8f91c946be8: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.objectStore(getStringFromWasm0(arg1, arg2));
            return ret;
        }, arguments); },
        __wbg_openCursor_2c5c6b17096f1cda: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.openCursor(arg1, __wbindgen_enum_IdbCursorDirection[arg2]);
            return ret;
        }, arguments); },
        __wbg_openCursor_62307c0eaa7b86e6: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.openCursor(arg1, __wbindgen_enum_IdbCursorDirection[arg2]);
            return ret;
        }, arguments); },
        __wbg_openKeyCursor_9f5cbac51e11d46c: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.openKeyCursor(arg1, __wbindgen_enum_IdbCursorDirection[arg2]);
            return ret;
        }, arguments); },
        __wbg_openKeyCursor_b74c90903ca1405d: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.openKeyCursor(arg1, __wbindgen_enum_IdbCursorDirection[arg2]);
            return ret;
        }, arguments); },
        __wbg_open_90f4e40d36c2c9f5: function() { return handleError(function (arg0, arg1, arg2, arg3) {
            const ret = arg0.open(getStringFromWasm0(arg1, arg2), arg3 >>> 0);
            return ret;
        }, arguments); },
        __wbg_parent_50abf7c590716318: function(arg0) {
            const ret = arg0.parent;
            return ret;
        },
        __wbg_pinNumber_145d31c4a0bdf38b: function(arg0) {
            const ret = arg0.pinNumber;
            return ret;
        },
        __wbg_pins_f94463194b4940c5: function(arg0, arg1) {
            const ret = arg1.pins;
            const ptr1 = passArrayJsValueToWasm0(ret, wasm.__wbindgen_malloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_preventDefault_f55c01cb5fd2bcc0: function(arg0) {
            arg0.preventDefault();
        },
        __wbg_primaryKey_17e12cb5b38ac14e: function() { return handleError(function (arg0) {
            const ret = arg0.primaryKey;
            return ret;
        }, arguments); },
        __wbg_process_b2fea42461d03994: function(arg0) {
            const ret = arg0.process;
            return ret;
        },
        __wbg_prototypesetcall_a6b02eb00b0f4ce2: function(arg0, arg1, arg2) {
            Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
        },
        __wbg_put_2d1082795891ee40: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.put(arg1);
            return ret;
        }, arguments); },
        __wbg_put_fd02d3070a55994c: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.put(arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_queueMicrotask_5d15a957e6aa920e: function(arg0) {
            queueMicrotask(arg0);
        },
        __wbg_queueMicrotask_f8819e5ffc402f36: function(arg0) {
            const ret = arg0.queueMicrotask;
            return ret;
        },
        __wbg_randomFillSync_ca9f178fb14c88cb: function() { return handleError(function (arg0, arg1) {
            arg0.randomFillSync(arg1);
        }, arguments); },
        __wbg_read_ddc2d178d2e57272: function(arg0) {
            const ret = arg0.read();
            return ret;
        },
        __wbg_readers_9f41c96c396b9f2f: function(arg0, arg1) {
            const ret = arg1.readers;
            const ptr1 = passArrayJsValueToWasm0(ret, wasm.__wbindgen_malloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_releaseLock_9baaf3ccc5cfad69: function(arg0) {
            arg0.releaseLock();
        },
        __wbg_remainingTries_53e3c9b198ae4e4d: function(arg0) {
            const ret = arg0.remainingTries;
            return isLikeNone(ret) ? 0x100000001 : (ret) >>> 0;
        },
        __wbg_removeEntry_6a65b8e731727aba: function(arg0, arg1, arg2) {
            const ret = arg0.removeEntry(getStringFromWasm0(arg1, arg2));
            return ret;
        },
        __wbg_require_7a9419e39d796c95: function() { return handleError(function () {
            const ret = module.require;
            return ret;
        }, arguments); },
        __wbg_resolve_e6c466bc1052f16c: function(arg0) {
            const ret = Promise.resolve(arg0);
            return ret;
        },
        __wbg_respond_008ca9525ae22847: function() { return handleError(function (arg0, arg1) {
            arg0.respond(arg1 >>> 0);
        }, arguments); },
        __wbg_result_f782323755b96dc8: function() { return handleError(function (arg0) {
            const ret = arg0.result;
            return ret;
        }, arguments); },
        __wbg_setTimeout_56bcdccbad22fd44: function() { return handleError(function (arg0, arg1) {
            const ret = setTimeout(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_setTimeout_b4a9096784635514: function(arg0, arg1) {
            const ret = setTimeout(arg0, arg1);
            return ret;
        },
        __wbg_set_022bee52d0b05b19: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = Reflect.set(arg0, arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_set_3bf1de9fab0cd644: function(arg0, arg1, arg2) {
            arg0[arg1 >>> 0] = arg2;
        },
        __wbg_set_3d484eb794afec82: function(arg0, arg1, arg2) {
            arg0.set(getArrayU8FromWasm0(arg1, arg2));
        },
        __wbg_set_6be42768c690e380: function(arg0, arg1, arg2) {
            arg0[arg1] = arg2;
        },
        __wbg_set_auto_increment_12f813bc0f283d18: function(arg0, arg1) {
            arg0.autoIncrement = arg1 !== 0;
        },
        __wbg_set_body_be11680f34217f75: function(arg0, arg1) {
            arg0.body = arg1;
        },
        __wbg_set_cache_968edea422613d1b: function(arg0, arg1) {
            arg0.cache = __wbindgen_enum_RequestCache[arg1];
        },
        __wbg_set_cause_9b863aef97bf7aac: function(arg0, arg1) {
            arg0.cause = arg1;
        },
        __wbg_set_create_6b7aba35286e014a: function(arg0, arg1) {
            arg0.create = arg1 !== 0;
        },
        __wbg_set_create_78b4812a4fac7eb8: function(arg0, arg1) {
            arg0.create = arg1 !== 0;
        },
        __wbg_set_credentials_6577be90e0e85eb6: function(arg0, arg1) {
            arg0.credentials = __wbindgen_enum_RequestCredentials[arg1];
        },
        __wbg_set_fde2cec06c23692b: function(arg0, arg1, arg2) {
            const ret = arg0.set(arg1, arg2);
            return ret;
        },
        __wbg_set_headers_50fc01786240a440: function(arg0, arg1) {
            arg0.headers = arg1;
        },
        __wbg_set_method_c9f1f985f6b6c427: function(arg0, arg1, arg2) {
            arg0.method = getStringFromWasm0(arg1, arg2);
        },
        __wbg_set_mode_5e08d503428c06b9: function(arg0, arg1) {
            arg0.mode = __wbindgen_enum_RequestMode[arg1];
        },
        __wbg_set_onerror_ed658d0b50b67e95: function(arg0, arg1) {
            arg0.onerror = arg1;
        },
        __wbg_set_onsuccess_bf03d6c06709ef09: function(arg0, arg1) {
            arg0.onsuccess = arg1;
        },
        __wbg_set_onupgradeneeded_3fc8649a687213f5: function(arg0, arg1) {
            arg0.onupgradeneeded = arg1;
        },
        __wbg_set_referrer_478d9a69d0d97a98: function(arg0, arg1, arg2) {
            arg0.referrer = getStringFromWasm0(arg1, arg2);
        },
        __wbg_set_referrer_policy_84fedaa88bc9d667: function(arg0, arg1) {
            arg0.referrerPolicy = __wbindgen_enum_ReferrerPolicy[arg1];
        },
        __wbg_set_signal_1d4e73c2305a0e7c: function(arg0, arg1) {
            arg0.signal = arg1;
        },
        __wbg_sign_2e7b4ff38c00ff21: function() { return handleError(function (arg0, arg1, arg2, arg3) {
            const ret = arg0.sign(getArrayU8FromWasm0(arg1, arg2), arg3);
            return ret;
        }, arguments); },
        __wbg_signal_fdc54643b47bf85b: function(arg0) {
            const ret = arg0.signal;
            return ret;
        },
        __wbg_size_cec5cae0e9d95a0b: function(arg0) {
            const ret = arg0.size;
            return ret;
        },
        __wbg_slice_8befbcfd02348841: function(arg0, arg1, arg2) {
            const ret = arg0.slice(arg1 >>> 0, arg2 >>> 0);
            return ret;
        },
        __wbg_stack_b956c8d3aa8afd04: function(arg0, arg1) {
            const ret = arg1.stack;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_static_accessor_GLOBAL_8cfadc87a297ca02: function() {
            const ret = typeof global === 'undefined' ? null : global;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_static_accessor_GLOBAL_THIS_602256ae5c8f42cf: function() {
            const ret = typeof globalThis === 'undefined' ? null : globalThis;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_static_accessor_SELF_e445c1c7484aecc3: function() {
            const ret = typeof self === 'undefined' ? null : self;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_static_accessor_WINDOW_f20e8576ef1e0f17: function() {
            const ret = typeof window === 'undefined' ? null : window;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_status_43e0d2f15b22d69f: function(arg0) {
            const ret = arg0.status;
            return ret;
        },
        __wbg_storage_11147bcf4dee71ae: function(arg0) {
            const ret = arg0.storage;
            return ret;
        },
        __wbg_storage_eecab26eeb654cf9: function(arg0) {
            const ret = arg0.storage;
            return ret;
        },
        __wbg_subarray_f8ca46a25b1f5e0d: function(arg0, arg1, arg2) {
            const ret = arg0.subarray(arg1 >>> 0, arg2 >>> 0);
            return ret;
        },
        __wbg_target_6d97e221d11b71b6: function(arg0) {
            const ret = arg0.target;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_then_792e0c862b060889: function(arg0, arg1, arg2) {
            const ret = arg0.then(arg1, arg2);
            return ret;
        },
        __wbg_then_8e16ee11f05e4827: function(arg0, arg1) {
            const ret = arg0.then(arg1);
            return ret;
        },
        __wbg_transaction_246d4ade615944eb: function(arg0) {
            const ret = arg0.transaction;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_transaction_9af5475ca3bd3fa9: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.transaction(arg1, __wbindgen_enum_IdbTransactionMode[arg2]);
            return ret;
        }, arguments); },
        __wbg_trustStatus_b6a732d839a85968: function(arg0) {
            const ret = arg0.trustStatus;
            return ret;
        },
        __wbg_type_0a57769f3b820c7f: function(arg0) {
            const ret = arg0.type;
            return (__wbindgen_enum_ObjectType.indexOf(ret) + 1 || 5) - 1;
        },
        __wbg_update_23505c717f36067e: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.update(arg1);
            return ret;
        }, arguments); },
        __wbg_url_2bf741820e6563a0: function(arg0, arg1) {
            const ret = arg1.url;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_validated_6846b477f4fd38bb: function(arg0) {
            const ret = arg0.validated;
            return ret;
        },
        __wbg_valueOf_25e11684e38026ef: function(arg0) {
            const ret = arg0.valueOf();
            return ret;
        },
        __wbg_valueOf_91a54faf8ce0976a: function(arg0) {
            const ret = arg0.valueOf();
            return ret;
        },
        __wbg_value_cd3e83709dea7716: function() { return handleError(function (arg0) {
            const ret = arg0.value;
            return ret;
        }, arguments); },
        __wbg_value_ee3a06f4579184fa: function(arg0) {
            const ret = arg0.value;
            return ret;
        },
        __wbg_values_1e6d547ce555ce44: function(arg0) {
            const ret = arg0.values();
            return ret;
        },
        __wbg_values_7972aa657cc4ef64: function(arg0) {
            const ret = arg0.values();
            return ret;
        },
        __wbg_versions_215a3ab1c9d5745a: function(arg0) {
            const ret = arg0.versions;
            return ret;
        },
        __wbg_view_701664ffb3b1ce67: function(arg0) {
            const ret = arg0.view;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_warn_3cc416af27dbdc02: function(arg0) {
            console.warn(arg0);
        },
        __wbg_write_e2ea1d4ca11fcc48: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.write(getArrayU8FromWasm0(arg1, arg2));
            return ret;
        }, arguments); },
        __wbindgen_cast_0000000000000001: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 2793, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1);
            return ret;
        },
        __wbindgen_cast_0000000000000002: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 5300, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h0f8e39b9d6884e2e);
            return ret;
        },
        __wbindgen_cast_0000000000000003: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 5435, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h6dd49a9e9a65024c);
            return ret;
        },
        __wbindgen_cast_0000000000000004: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("ArrayBuffer")], shim_idx: 2793, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1_3);
            return ret;
        },
        __wbindgen_cast_0000000000000005: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("Event")], shim_idx: 3407, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h556375090e4162ea);
            return ret;
        },
        __wbindgen_cast_0000000000000006: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("Event")], shim_idx: 3454, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h80e1115653e1e758);
            return ret;
        },
        __wbindgen_cast_0000000000000007: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("IDBVersionChangeEvent")], shim_idx: 3409, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h0efde9d7160941ea);
            return ret;
        },
        __wbindgen_cast_0000000000000008: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("IteratorResult<any>")], shim_idx: 3572, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h68396f19537a4bed);
            return ret;
        },
        __wbindgen_cast_0000000000000009: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("string")], shim_idx: 2793, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1_8);
            return ret;
        },
        __wbindgen_cast_000000000000000a: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [], shim_idx: 4288, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__ha49d0df5f4081b25);
            return ret;
        },
        __wbindgen_cast_000000000000000b: function(arg0, arg1) {
            // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [], shim_idx: 5273, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
            const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h7d0e5d7b58498267);
            return ret;
        },
        __wbindgen_cast_000000000000000c: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_cast_000000000000000d: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(U8)) -> NamedExternref("Uint8Array")`.
            const ret = getArrayU8FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_cast_000000000000000e: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_cast_000000000000000f: function(arg0) {
            // Cast intrinsic for `U64 -> Externref`.
            const ret = BigInt.asUintN(64, arg0);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./libparsec_bindings_web_bg.js": import0,
    };
}

function wasm_bindgen__convert__closures_____invoke__ha49d0df5f4081b25(arg0, arg1) {
    wasm.wasm_bindgen__convert__closures_____invoke__ha49d0df5f4081b25(arg0, arg1);
}

function wasm_bindgen__convert__closures_____invoke__h7d0e5d7b58498267(arg0, arg1) {
    wasm.wasm_bindgen__convert__closures_____invoke__h7d0e5d7b58498267(arg0, arg1);
}

function wasm_bindgen__convert__closures_____invoke__h0f8e39b9d6884e2e(arg0, arg1, arg2) {
    wasm.wasm_bindgen__convert__closures_____invoke__h0f8e39b9d6884e2e(arg0, arg1, arg2);
}

function wasm_bindgen__convert__closures_____invoke__h80e1115653e1e758(arg0, arg1, arg2) {
    wasm.wasm_bindgen__convert__closures_____invoke__h80e1115653e1e758(arg0, arg1, arg2);
}

function wasm_bindgen__convert__closures_____invoke__h0efde9d7160941ea(arg0, arg1, arg2) {
    wasm.wasm_bindgen__convert__closures_____invoke__h0efde9d7160941ea(arg0, arg1, arg2);
}

function wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h6dd49a9e9a65024c(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h6dd49a9e9a65024c(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1_3(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1_3(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h556375090e4162ea(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h556375090e4162ea(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h68396f19537a4bed(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h68396f19537a4bed(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1_8(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h0548abdd640015d1_8(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h882fceb916efd94b(arg0, arg1, arg2, arg3) {
    wasm.wasm_bindgen__convert__closures_____invoke__h882fceb916efd94b(arg0, arg1, arg2, arg3);
}


const __wbindgen_enum_IdbCursorDirection = ["next", "nextunique", "prev", "prevunique"];


const __wbindgen_enum_IdbTransactionMode = ["readonly", "readwrite", "versionchange", "readwriteflush", "cleanup"];


const __wbindgen_enum_ObjectType = ["certificate", "publicKey", "privateKey", "dataContainer"];


const __wbindgen_enum_ReadableStreamType = ["bytes"];


const __wbindgen_enum_ReferrerPolicy = ["", "no-referrer", "no-referrer-when-downgrade", "origin", "origin-when-cross-origin", "unsafe-url", "same-origin", "strict-origin", "strict-origin-when-cross-origin"];


const __wbindgen_enum_RequestCache = ["default", "no-store", "reload", "no-cache", "force-cache", "only-if-cached"];


const __wbindgen_enum_RequestCredentials = ["omit", "same-origin", "include"];


const __wbindgen_enum_RequestMode = ["same-origin", "no-cors", "cors", "navigate"];
const EmailAddressFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_emailaddress_free(ptr >>> 0, 1));
const IntoUnderlyingByteSourceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_intounderlyingbytesource_free(ptr >>> 0, 1));
const IntoUnderlyingSinkFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_intounderlyingsink_free(ptr >>> 0, 1));
const IntoUnderlyingSourceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_intounderlyingsource_free(ptr >>> 0, 1));
const ParsingOptionsFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsingoptions_free(ptr >>> 0, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

const CLOSURE_DTORS = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(state => wasm.__wbindgen_destroy_closure(state.a, state.b));

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

function getStringFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return decodeText(ptr, len);
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function makeMutClosure(arg0, arg1, f) {
    const state = { a: arg0, b: arg1, cnt: 1 };
    const real = (...args) => {

        // First up with a closure we increment the internal reference
        // count. This ensures that the Rust closure environment won't
        // be deallocated while we're invoking it.
        state.cnt++;
        const a = state.a;
        state.a = 0;
        try {
            return f(a, state.b, ...args);
        } finally {
            state.a = a;
            real._wbg_cb_unref();
        }
    };
    real._wbg_cb_unref = () => {
        if (--state.cnt === 0) {
            wasm.__wbindgen_destroy_closure(state.a, state.b);
            state.a = 0;
            CLOSURE_DTORS.unregister(state);
        }
    };
    CLOSURE_DTORS.register(real, state, state);
    return real;
}

function passArrayJsValueToWasm0(array, malloc) {
    const ptr = malloc(array.length * 4, 4) >>> 0;
    for (let i = 0; i < array.length; i++) {
        const add = addToExternrefTable0(array[i]);
        getDataViewMemory0().setUint32(ptr + 4 * i, add, true);
    }
    WASM_VECTOR_LEN = array.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasm;
function __wbg_finalize_init(instance, module) {
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = module.ok && expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = "/client/assets/libparsec_bindings_web_bg-937efcf4.wasm";
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
